import { FC, useState } from 'react';

import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { CloseSvg } from '@/Components/Icons/CloseSvg';
import { Switch } from '@/Components/Switch';
import { Button } from '@/Components/Button';
import { useContentLabelUiData } from '@/hook/useContentLabelUiData';
import { getContentLabel } from '@/util/contentLabel.util';

type AiVideoLabelModalProps = AppModalProps<{
    // NOTE: Not read here -- lets the URL sync know which reel this is for.
    reelCode?: string;
    initialIsAiAudio?: boolean;
    initialIsAiVideo?: boolean;
    submitButtonLabel?: string;
    cancelButtonLabel?: string;
    onSave: (payload: { isAiAudio: boolean; isAiVideo: boolean }) => void;
}>;

export const AiVideoLabelModal: FC<AiVideoLabelModalProps> = ({
    payload,
    onClose,
}) => {
    const {
        initialIsAiAudio,
        initialIsAiVideo,
        submitButtonLabel,
        cancelButtonLabel,
        onSave,
    } = payload;

    const [isAiAudio, setIsAiAudio] = useState(initialIsAiAudio ?? false);
    const [isAiVideo, setIsAiVideo] = useState(initialIsAiVideo ?? false);

    const contentLabel = getContentLabel({ isAiAudio, isAiVideo });

    const contentMap = useContentLabelUiData(20);

    const onSaveClick = () => {
        onSave({ isAiAudio, isAiVideo });
        onClose();
    };

    return (
        <ModalView
            maxWidth="lg"
            onClose={onClose}
            contentContainerClassName="py-6 text-brand-ocean flex flex-col justify-center items-center">
            <div className="flex flex-row items-center w-full px-6">
                <div className="flex-1 text-heading-four !font-bold text-brand-ocean text-left">
                    Label your video
                </div>
                <button
                    title="Close"
                    aria-label="Close"
                    className="p-2 -mr-1 transition-all rounded-md hover:cursor-pointer hover:bg-neutral-mid"
                    onClick={onClose}>
                    <CloseSvg className="[&_path]:stroke-1 [&_path]:stroke-brand-ocean" />
                </button>
            </div>
            <div className="flex flex-col gap-y-4 mt-6 mx-4 w-full px-6">
                <div className="flex flex-row items-center justify-start">
                    <Switch
                        id="isAiAudio"
                        value={isAiAudio}
                        onToggle={() => setIsAiAudio(!isAiAudio)}
                    />
                    <label htmlFor="isAiAudio" className="ml-4 text-body-text">
                        Makes use of AI-generated audio
                    </label>
                </div>
                <div className="flex flex-row items-center justify-start">
                    <Switch
                        id="isAiVideo"
                        value={isAiVideo}
                        onToggle={() => setIsAiVideo(!isAiVideo)}
                    />
                    <label htmlFor="isAiVideo" className="ml-4 text-body-text">
                        Makes use of AI-generated video footage
                    </label>
                </div>
                <div className="mt-2 relative bg-brand-red flex items-center justify-center rounded-xl overflow-hidden">
                    <svg
                        width="322"
                        height="240"
                        viewBox="0 0 322 240"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <circle cx="161" cy="120" r="104" fill="#F99E00" />
                        <circle
                            opacity="0.5"
                            cx="161"
                            cy="120"
                            r="161"
                            fill="#F99E00"
                        />
                        <g opacity="0.9">
                            <path
                                d="M110.334 121.917C110.334 93.9345 133.018 71.2503 161 71.2503C188.983 71.2503 211.667 93.9345 211.667 121.917C211.667 149.899 188.983 172.584 161 172.584C133.018 172.584 110.334 149.899 110.334 121.917Z"
                                fill="#185874"
                            />
                            <path
                                d="M146.868 188.417C136.507 188.414 129.437 188.412 123.374 190.036C106.982 194.428 94.1783 207.232 89.7861 223.624C88.1616 229.687 88.1639 236.757 88.1673 247.118L88.1676 255.657C88.1672 256.418 88.1667 257.411 88.2293 258.302C88.3028 259.347 88.4952 260.944 89.2415 262.686C90.5869 265.828 93.0902 268.332 96.2321 269.677C97.9747 270.423 99.572 270.616 100.617 270.689C101.508 270.752 102.501 270.751 103.262 270.751H218.74C219.501 270.751 220.494 270.752 221.385 270.689C222.43 270.616 224.027 270.423 225.77 269.677C228.912 268.332 231.415 265.828 232.76 262.686C233.507 260.944 233.699 259.347 233.773 258.302C233.835 257.411 233.835 256.418 233.834 255.657L233.835 247.117C233.838 236.757 233.84 229.687 232.216 223.624C227.824 207.232 215.02 194.428 198.628 190.036C192.565 188.412 185.495 188.414 175.134 188.417H146.868Z"
                                fill="#185874"
                            />
                        </g>
                        <circle cx="161" cy="120" r="54" fill="#186179" />
                    </svg>
                    <div className="leading-none flex flex-row items-center justify-center px-2 py-1.5 bg-neutral-darker text-body-text text-gray-10 absolute top-3 left-3 rounded-lg">
                        <div className="mr-1">
                            {contentMap[contentLabel].icon}
                        </div>
                        {contentMap[contentLabel].label}
                    </div>
                </div>
                <div className="text-small-text-13 text-gray-80">
                    Visitors on your website will see this label on the Player
                </div>
                <div className="mt-2 flex flex-row items-center gap-x-4">
                    <Button
                        variant="default"
                        className="flex-1 h-9"
                        onClick={onClose}>
                        {cancelButtonLabel || 'Cancel'}
                    </Button>
                    <Button
                        variant="primary"
                        className="flex-1 h-9"
                        onClick={onSaveClick}>
                        {submitButtonLabel || 'Save'}
                    </Button>
                </div>
            </div>
        </ModalView>
    );
};
